import type { ReportSectionKind } from './report-outline';

/**
 * One fixed AML case for every agent: the builder has no run to draw real values from, and values
 * built from this agent would read as output it had already produced.
 */
export const REPORT_EXAMPLES: Record<Exclude<ReportSectionKind, 'narrative' | 'custom'>, unknown> = {
  callout: {
    title: 'Scope of this document',
    body: 'Prepared for a human reviewer, who confirms the disposition before anything is filed.',
    severity: 'neutral',
  },
  record: {
    name: 'Daniel R. Vega',
    monogram: 'DV',
    subtitle: 'DOB 1989-04-12 · Checking ····9017 · onboarded Jan 2025',
    badge: { label: 'Under review', tone: 'warning' },
    fields: [
      { label: 'Occupation (stated)', value: 'Rideshare driver, self-employed' },
      { label: 'Expected activity', value: 'Payroll ≤ $4,000 / month' },
      { label: 'Activity reviewed', value: '$312,450 in 47 credits', tone: 'warning' },
      { label: 'Prior SARs', value: 'None' },
    ],
  },
  stat: {
    title: 'Structure snapshot',
    stats: [
      { label: 'Directors', value: '1' },
      { label: 'UBOs (>25%)', value: '1' },
      { label: 'Corporate shareholders', value: '0' },
      { label: 'Sanctions hits', value: '0', tone: 'positive' },
    ],
  },
  'data-view': {
    title: 'Credits by week, against a $4,000 baseline',
    categories: ['Mar 3', 'Mar 10', 'Mar 17'],
    series: [{ name: 'Credits', values: [118400, 96300, 94550] }],
    defaultView: 'bar',
    format: 'currency',
    referenceLine: { label: 'Baseline', value: 4000 },
  },
  checklist: {
    title: 'Lists screened',
    items: [
      { label: 'OFAC SDN', state: 'complete', detail: 'No match' },
      { label: 'Adverse media', state: 'complete', detail: 'No confirmed match' },
      { label: 'PEP', state: 'blocked', detail: 'Provider returned no result — re-run required' },
    ],
  },
  timeline: {
    title: 'What happened, in order',
    events: [
      { label: 'Alert raised', date: '18 Mar 2026', detail: 'Structuring rule, 47 credits' },
      { label: 'Customer profile reviewed', date: '18 Mar 2026' },
      { label: 'Screening completed', date: '19 Mar 2026', tone: 'warning', detail: 'PEP incomplete' },
    ],
  },
  quote: {
    text: 'Funds are proceeds from vehicle sales handled on behalf of family members.',
    source: 'Customer statement, recorded call',
    date: '19 Mar 2026',
    verified: false,
  },
  compare: {
    title: 'Stated against observed',
    columns: [
      {
        title: 'Stated at onboarding',
        items: [
          { label: 'Monthly inflow', value: '≤ $4,000' },
          { label: 'Source', value: 'Payroll' },
        ],
      },
      {
        title: 'Observed this period',
        tone: 'warning',
        items: [
          { label: 'Monthly inflow', value: '$312,450' },
          { label: 'Source', value: 'Third-party cash deposits' },
        ],
      },
    ],
  },
  cluster: {
    title: 'Related parties',
    edges: [
      {
        source: 'SWIFAY LIMITED',
        target: 'KLOZEY LIMITED',
        attribute: 'Shared controlling person',
        detail: 'Common director only — no ownership link',
        evidence: ['S11'],
      },
    ],
  },
  funnel: {
    title: 'Alert population reviewed',
    format: 'integer',
    stages: [
      { label: 'Credits in period', value: 47 },
      { label: 'Above threshold', value: 31 },
      { label: 'Unexplained', value: 12, tone: 'warning' },
    ],
  },
  decision: {
    title: 'Disposition',
    outcome: 'File SAR',
    severity: 'critical',
    headline: 'Activity is inconsistent with the stated profile and unexplained after outreach.',
    rationale: 'Inflow exceeds the stated baseline by roughly 78×, and the explanation offered is unverified.',
  },
};
